<script setup>
import { ref, computed, nextTick, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAdmin } from 'dashboard/composables/useAdmin';
import powerDataAPI from 'dashboard/api/powerData';

import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import DatasourcesTab from './components/DatasourcesTab.vue';
import AddSourceDialog from './components/AddSourceDialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import SettingsTab from './components/SettingsTab.vue';
import PowerDataShell from './components/PowerDataShell.vue';

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const router = useRouter();
const { isSuperAdmin } = useAdmin();

const storeId = route.params.storeId;
const accountId = store.getters.getCurrentAccountId;

const storeData = ref({ name: '', source_count: 0, language: 'portuguese' });
const isFetching = ref(true);
const dsKey = ref(0);

// Duas abas, não três. A de conexões saiu porque quem liga um agente a uma base
// é a tela do PowerAgent, e ter os dois lados dava dois lugares para a mesma
// ligação. A de adicionar virou botão: era uma aba inteira para uma ação
// pontual, e aba sugere um lugar onde se permanece.
//
// Configurações só para o super admin (POWERDATA-ACESSO-01, 30/09/2026): tudo o que ela tem
// é da BASE — nome, idioma, etiquetas automáticas e apagar —, e o servidor recusa a base a
// qualquer outro papel. Mostrada ao administrador e ao atendente, era um formulário em que
// todo "Salvar" dava erro. As fontes e o "Adicionar" ficam para os três.
const tabs = computed(() => [
  { key: 'datasources', label: t('POWER_DATA.TABS.SOURCES') },
  ...(isSuperAdmin.value
    ? [{ key: 'settings', label: t('POWER_DATA.TABS.SETTINGS') }]
    : []),
]);

const addDialogRef = ref(null);

const activeTabIndex = computed(() => {
  const idx = tabs.value.findIndex(tab => tab.key === route.query.tab);
  return idx === -1 ? 0 : idx;
});

const activeTab = computed(() => tabs.value[activeTabIndex.value].key);

// A troca de aba e a espera mais longa desta tela, e por um motivo estrutural:
// as duas abas sao `v-if`/`v-else-if`, entao sair de Sources DESTROI as 1.186
// linhas (~12 componentes cada) de uma vez, e voltar reconstroi tudo. Medido em
// 09/09: ~5 s para abrir Configuracoes na base 9.
//
// Isto aqui nao torna a troca rapida — torna a espera VISIVEL. O mesmo cuidado
// do toggleSite vale: sem ceder um quadro pintado, o indicador entraria e
// sairia no mesmo flush do Vue, depois da espera que ele cobre.
const trocandoAba = ref(false);

const proximoQuadroPintado = () =>
  new Promise(resolve => {
    requestAnimationFrame(() => requestAnimationFrame(resolve));
  });

async function onTabChange(tab) {
  if (tab.key === activeTab.value) return;
  trocandoAba.value = true;
  await proximoQuadroPintado();
  try {
    await router.replace({ query: { tab: tab.key } });
    await nextTick();
  } finally {
    trocandoAba.value = false;
  }
}

async function fetchStoreData() {
  isFetching.value = true;
  try {
    storeData.value = await powerDataAPI.show(accountId, storeId);
  } catch {
    /* o cabeçalho degrada para o nome vazio; a aba mostra o próprio erro */
  } finally {
    isFetching.value = false;
  }
}

function onSourceAdded() {
  dsKey.value += 1;
  router.replace({ query: { tab: 'datasources' } });
  fetchStoreData();
}

function abrirAdicionar() {
  addDialogRef.value?.open();
}

onMounted(fetchStoreData);
</script>

<template>
  <PowerDataShell :title="t('POWER_DATA.TITLE')">
    <SettingsLayout
      :is-loading="isFetching"
      :loading-message="t('POWER_DATA.LOADING')"
      body-class="w-full max-w-[1150px] mx-auto"
    >
      <template #header>
        <!-- COLUNA-PZ: cabecalho e PAINEL na mesma largura, os dois centralizados.
           Duas tentativas antes desta, e o que cada uma errou:
           1) `max-w` so no CONTEUDO: o painel do SettingsLayout continuava de
              ponta a ponta, entao numa tela de ~1600px sobravam ~225px de card
              VAZIO de cada lado, com a tabela ilhada no meio.
           2) largura cheia com a coluna do Nome absorvendo a folga: acabou com
              o card vazio, mas jogou 260px de espaco morto DENTRO da linha,
              entre o nome (que trunca em 26rem) e a coluna Tipo.
           O que resolve os dois e estreitar o PAINEL, nao o conteudo: a tabela
           passa a preencher o card de borda a borda e o card fica centralizado.
           A medida sai da soma real das colunas (~1096px medidos no navegador)
           mais o px-4 do painel; ver LARGURA-COLUNAS no DatasourcesTab.
           A MEDIDA APARECE DUAS VEZES neste arquivo — aqui e no `body-class` do
           SettingsLayout logo acima. Tentei uma variavel CSS e ela so resolvia
           dentro de `main`, deixando o cabecalho sem limite: o painel encolhia e
           o titulo continuava largo. Se mudar uma, mude a outra. -->
        <div class="w-full max-w-[1150px] mx-auto">
          <BaseSettingsHeader
            :title="storeData.name"
            :description="t('POWER_DATA.SHOW_DESCRIPTION')"
            :back-button-label="t('POWER_DATA.TITLE')"
            :back-url="{ name: 'power_data_index' }"
          >
            <template #count>
              <span class="text-body-main text-n-slate-11">
                {{
                  t('POWER_DATA.LIST.SOURCE_COUNT', {
                    n: storeData.source_count || 0,
                  })
                }}
              </span>
            </template>
            <template #actions>
              <Button
                :label="t('POWER_DATA.ADD.BUTTON')"
                icon="i-lucide-plus"
                size="sm"
                @click="abrirAdicionar"
              />
            </template>
            <template #tabs>
              <TabBar
                :tabs="tabs"
                :initial-active-tab="activeTabIndex"
                class="mt-4"
                @tab-changed="onTabChange"
              />
            </template>
          </BaseSettingsHeader>
        </div>
      </template>

      <template #body>
        <!-- O painel ja esta na largura certa (body-class acima); aqui so o
           posicionamento da sobreposicao de "trocando aba". -->
        <div class="relative w-full">
          <!-- SOBREPOSICAO, nao substituicao. Se este bloco trocasse a aba, o
             proprio ato de liga-lo ja desmontaria as 1.186 linhas — o trabalho
             caro aconteceria ANTES da pintura e o indicador chegaria tarde de
             novo. Como camada por cima, ligar custa quase nada, o navegador
             pinta, e so entao a troca de aba roda, com o aviso ja na tela. -->
          <div
            v-if="trocandoAba"
            class="absolute inset-0 z-10 flex items-start justify-center gap-2 pt-20 bg-n-solid-1/70 rounded-xl pointer-events-none text-body-main text-n-slate-11"
          >
            <i class="i-lucide-loader-circle animate-spin" />
            {{ t('POWER_DATA.SOURCES.LOADING') }}
          </div>
          <!-- keep-alive, e não o v-if solto que estava aqui: sair de Sources DESTRUÍA as
             linhas (209 raízes, ~5.700 nós de DOM na base 9) e voltar reconstruía tudo.
             Medido em jsdom sobre as 1.388 fontes da base 9: 640 ms para desmontar e
             2.250 ms para montar de novo — perto de três segundos por ida e volta, gastos
             para reconstruir exatamente o que acabou de ser jogado fora. Em cache, a
             troca não custa nem desmontagem nem remontagem. -->
          <keep-alive>
            <DatasourcesTab
              v-if="activeTab === 'datasources'"
              :key="dsKey"
              :store-id="storeId"
            />
          </keep-alive>
          <SettingsTab
            v-if="activeTab === 'settings'"
            :store-id="storeId"
            :power-data="storeData"
            @updated="fetchStoreData"
          />
        </div>
      </template>

      <AddSourceDialog
        ref="addDialogRef"
        :store-id="storeId"
        @added="onSourceAdded"
      />
    </SettingsLayout>
  </PowerDataShell>
</template>
